import { h, FunctionComponent, JSX } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import { IvrNodeType, IvrFlowConfiguration } from '../types/ivr';

// i18n helper using Rails translations
const t = (key: string, fallback?: string): string => {
  const keys = key.split('.');
  let value: any = (window as any).I18n?.call_center;
  for (const k of keys) {
    value = value?.[k];
    if (!value) break;
  }
  return value || fallback || key;
};

interface IvrFlowDesignerProps {
  flowId: string;
  initialConfiguration: IvrFlowConfiguration;
  saveUrl: string;
}

interface FlowNode {
  id: string;
  type: IvrNodeType;
  x: number;
  y: number;
  label: string;
  config: Record<string, unknown>;
}

interface FlowEdge {
  id: string;
  from: string;
  to: string;
  sourceHandle?: string;
}

interface MenuOption {
  dtmf: string;
  label: string;
}

interface SourceHandle {
  id: string;
  label: string;
  badgeColor: string;
  textColor?: string;
  caption?: string;
}

const NODE_CONTENT_WIDTH = 220;
const NODE_PADDING = 16;
const NODE_BORDER = 2;
/** Padding-box width (inside border) — absolute handles are relative to this. */
const NODE_INNER_WIDTH = NODE_CONTENT_WIDTH + NODE_PADDING * 2;
const HANDLE_SIZE = 16;
const HANDLE_TOP_BASE = 56;
const HANDLE_SPACING = 28;
/** Title row in the content box: marginTop 12 + height 44 + marginBottom 10. */
const TITLE_BLOCK = 66;
const BRANCH_ROW = 28;

const hasBranchCaptions = (node: FlowNode): boolean => getSourceHandles(node).some(h => h.caption);

/** Top of a source handle, measured from the node's padding edge. */
const handleTop = (node: FlowNode, index: number): number => {
  if (hasBranchCaptions(node)) {
    return NODE_PADDING + TITLE_BLOCK + index * BRANCH_ROW + (BRANCH_ROW - HANDLE_SIZE) / 2;
  }
  return HANDLE_TOP_BASE + index * HANDLE_SPACING;
};
const MIN_ZOOM = 0.35;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.1;

const toVarDisplay = (name: string): string => {
  if (!name) return '';
  return name.startsWith('$') ? name : `$${name}`;
};

const fromVarInput = (raw: string): string => raw.replace(/^\$+/, '').trim();

const generateId = (): string => Math.random().toString(36).substr(2, 9);

const INCOMING_NODE_ID = 'incoming';

const incomingNodeMeta = () => ({
  type: 'incoming' as IvrNodeType,
  icon: '📥',
  color: '#0ea5e9',
  title: t('node_types.incoming.title', 'Incoming call'),
  desc: t('node_types.incoming.description', 'Call entry point')
});

const isIncomingNode = (node: { id: string; type: IvrNodeType }): boolean =>
  node.id === INCOMING_NODE_ID || node.type === 'incoming';

const buildIncomingNode = (): FlowNode => ({
  id: INCOMING_NODE_ID,
  type: 'incoming',
  x: 40,
  y: 160,
  label: t('node_types.incoming.title', 'Incoming call'),
  config: {}
});

const ensureIncomingNodes = (nodes: FlowNode[]): FlowNode[] => {
  const found = nodes.find(isIncomingNode);
  const others = nodes.filter(n => !isIncomingNode(n));
  const incoming = found
    ? { ...found, id: INCOMING_NODE_ID, type: 'incoming' as IvrNodeType }
    : buildIncomingNode();
  return [incoming, ...others];
};

const migrateMenuOptions = (options: unknown): MenuOption[] => {
  if (!options) {
    return [
      { dtmf: '1', label: '' },
      { dtmf: '2', label: '' }
    ];
  }
  if (typeof options === 'string') {
    return options
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .map(dtmf => ({ dtmf, label: dtmf }));
  }
  if (Array.isArray(options)) {
    if (options.length === 0) return [{ dtmf: '1', label: '' }];
    if (typeof options[0] === 'string') {
      return (options as string[]).map(dtmf => ({ dtmf, label: dtmf }));
    }
    return options as MenuOption[];
  }
  return [{ dtmf: '1', label: '' }];
};

const migrateNodeConfig = (type: IvrNodeType, config: Record<string, unknown>): Record<string, unknown> => {
  const c = { ...config };

  switch (type) {
    case 'play_message':
      if (c.text && !c.content) c.content = c.text;
      delete c.text;
      break;
    case 'menu':
      if (c.text && !c.introMessage) c.introMessage = c.text;
      delete c.text;
      c.options = migrateMenuOptions(c.options);
      if (c.timeoutSeconds == null) c.timeoutSeconds = 10;
      if (c.maxAttempts == null) c.maxAttempts = 3;
      break;
    case 'collect_input':
      if (c.text && !c.promptMessage) c.promptMessage = c.text;
      delete c.text;
      if (c.variable && !c.variableName) c.variableName = c.variable;
      delete c.variable;
      break;
    case 'ai_agent':
      if (c.welcome && !c.systemPrompt) c.systemPrompt = c.welcome;
      delete c.welcome;
      delete c.agentType;
      break;
    case 'voicemail':
      if (c.text && !c.introMessage) c.introMessage = c.text;
      delete c.text;
      break;
    case 'condition':
      if (c.variable && !c.variableName) c.variableName = c.variable;
      delete c.variable;
      break;
    default:
      break;
  }

  return c;
};

const getDefaultConfig = (type: IvrNodeType): Record<string, unknown> => {
  switch (type) {
    case 'play_message':
      return { content: t('nodes.play_message.default_text', 'Welcome!') };
    case 'menu':
      return {
        introMessage: t('nodes.menu.default_text', 'Please choose: 1 - Sales, 2 - Support'),
        options: [
          { dtmf: '1', label: t('nodes.menu.option_label', 'Option') + ' 1' },
          { dtmf: '2', label: t('nodes.menu.option_label', 'Option') + ' 2' }
        ],
        timeoutSeconds: 10,
        maxAttempts: 3
      };
    case 'collect_input':
      return {
        variableName: 'identifier',
        promptMessage: t('nodes.collect_input.default_text', 'Please enter your identifier')
      };
    case 'ai_agent':
      return { systemPrompt: '' };
    case 'transfer':
      return { transferType: 'operator', operatorTarget: 'rules', destination: '', personGroupId: '', personId: '', ringTimeoutSeconds: 30, onNoAnswer: 'continue' };
    case 'set_person_group':
      return { personGroupId: '' };
    case 'voicemail':
      return { introMessage: t('nodes.voicemail.default_text', 'Please leave a message after the tone') };
    case 'condition':
      return { variableName: '', operator: 'equals', value: '' };
    case 'identify_caller':
      return {};
    default:
      return {};
  }
};

const getSourceHandles = (node: FlowNode): SourceHandle[] => {
  switch (node.type) {
    case 'menu': {
      const options = (node.config?.options as MenuOption[]) || [];
      const handles: SourceHandle[] = options.map(opt => ({
        id: opt.dtmf,
        label: opt.dtmf,
        badgeColor: '#8b5cf6',
        textColor: '#ffffff'
      }));
      handles.push({
        id: 'timeout',
        label: '•',
        badgeColor: '#64748b',
        textColor: '#ffffff'
      });
      return handles;
    }
    case 'condition':
      return [
        { id: 'true', label: '', caption: t('nodes.condition.true_branch', 'Yes'), badgeColor: '#10b981', textColor: '#ffffff' },
        { id: 'false', label: '', caption: t('nodes.condition.false_branch', 'No'), badgeColor: '#ef4444', textColor: '#ffffff' }
      ];
    case 'identify_caller':
      return [
        { id: 'true', label: '', caption: t('nodes.identify_caller.true_branch', 'Recognized'), badgeColor: '#10b981', textColor: '#ffffff' },
        { id: 'false', label: '', caption: t('nodes.identify_caller.false_branch', 'Unknown'), badgeColor: '#ef4444', textColor: '#ffffff' }
      ];
    default:
      return [{ id: 'default', label: '+', badgeColor: '#3b82f6', textColor: '#ffffff' }];
  }
};

const edgeHandleKey = (handle?: string): string => handle || 'default';

/** Source handle center in world coords — handle is half outside the right padding edge. */
const getHandlePosition = (node: FlowNode, handleId: string): { x: number; y: number } => {
  const handles = getSourceHandles(node);
  const index = Math.max(0, handles.findIndex(h => h.id === handleId));
  return {
    x: node.x + NODE_BORDER + NODE_INNER_WIDTH,
    y: node.y + NODE_BORDER + handleTop(node, index) + HANDLE_SIZE / 2
  };
};

/** Target handle center — half outside the left padding edge. */
const getTargetPosition = (node: FlowNode): { x: number; y: number } => ({
  x: node.x + NODE_BORDER,
  y: node.y + NODE_BORDER + HANDLE_TOP_BASE + HANDLE_SIZE / 2
});

const getNodeMinHeight = (node: FlowNode): number => {
  const handleCount = getSourceHandles(node).length;
  if (hasBranchCaptions(node)) {
    return TITLE_BLOCK + handleCount * BRANCH_ROW + 8;
  }
  return Math.max(80, HANDLE_TOP_BASE + (handleCount - 1) * HANDLE_SPACING + HANDLE_SIZE + 16);
};

const buildEdgePath = (sx: number, sy: number, tx: number, ty: number): string => {
  const dx = Math.max(48, Math.abs(tx - sx) * 0.45);
  return `M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`;
};

const clampZoom = (z: number): number => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

const getNodeTypes = () => [
  { type: 'play_message' as IvrNodeType, icon: '🔊', color: '#3b82f6', title: t('node_types.play_message.title', 'Play Message'), desc: t('node_types.play_message.description', 'Play TTS or audio') },
  { type: 'menu' as IvrNodeType, icon: '📋', color: '#8b5cf6', title: t('node_types.menu.title', 'Menu'), desc: t('node_types.menu.description', 'DTMF keypad selection') },
  { type: 'collect_input' as IvrNodeType, icon: '⌨️', color: '#10b981', title: t('node_types.collect_input.title', 'Collect Input'), desc: t('node_types.collect_input.description', 'Collect caller data') },
  { type: 'ai_agent' as IvrNodeType, icon: '🤖', color: '#06b6d4', title: t('node_types.ai_agent.title', 'AI Agent'), desc: t('node_types.ai_agent.description', 'AI conversation') },
  { type: 'transfer' as IvrNodeType, icon: '📞', color: '#f59e0b', title: t('node_types.transfer.title', 'Transfer'), desc: t('node_types.transfer.description', 'Transfer the call') },
  { type: 'set_person_group' as IvrNodeType, icon: '👥', color: '#14b8a6', title: t('node_types.set_person_group.title', 'Set person group'), desc: t('node_types.set_person_group.description', 'Override operator group') },
  { type: 'voicemail' as IvrNodeType, icon: '💬', color: '#ef4444', title: t('node_types.voicemail.title', 'Voicemail'), desc: t('node_types.voicemail.description', 'Record a message') },
  { type: 'identify_caller' as IvrNodeType, icon: '🔍', color: '#0ea5e9', title: t('node_types.identify_caller.title', 'Caller recognition'), desc: t('node_types.identify_caller.description', 'Branch on SIP caller match') },
  { type: 'condition' as IvrNodeType, icon: '◈', color: '#6366f1', title: t('node_types.condition.title', 'Condition'), desc: t('node_types.condition.description', 'Branch on data') }
];

export const IvrFlowDesigner: FunctionComponent<IvrFlowDesignerProps> = ({
  initialConfiguration,
  saveUrl
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [nodes, setNodes] = useState<FlowNode[]>([]);
  const [edges, setEdges] = useState<FlowEdge[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectFrom, setConnectFrom] = useState<string | null>(null);
  const [connectFromHandle, setConnectFromHandle] = useState<string | null>(null);
  const [connectMousePos, setConnectMousePos] = useState<{ x: number; y: number } | null>(null);
  const [showHelp, setShowHelp] = useState(true);
  const [conditionHelpId, setConditionHelpId] = useState<string | null>(null);
  const didInitialFit = useRef(false);
  const [saved, setSaved] = useState(false);
  const [nodeTypes] = useState(getNodeTypes());
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 40, y: 40 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const spaceHeld = useRef(false);
  const didPan = useRef(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !(e.target as HTMLElement)?.matches?.('input, textarea, select')) {
        spaceHeld.current = true
        e.preventDefault()
      }
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceHeld.current = false
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [])

  const clientToWorld = (clientX: number, clientY: number): { x: number; y: number } => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return {
      x: (clientX - rect.left - pan.x) / zoom,
      y: (clientY - rect.top - pan.y) / zoom
    };
  };

  useEffect(() => {
    const parsedNodes = (initialConfiguration.nodes || []).map((n: any) => {
      const type = (n.data?.type || 'play_message') as IvrNodeType;
      return {
        id: n.id,
        type,
        x: n.position?.x || 100,
        y: n.position?.y || 100,
        label: n.data?.label || t('node_types.play_message.title', 'Play Message'),
        config: migrateNodeConfig(type, n.data?.config || {})
      };
    });

    const parsedEdges = (initialConfiguration.edges || []).map((e: any) => ({
      id: e.id,
      from: e.source,
      to: e.target,
      sourceHandle: e.sourceHandle
    }));

    setNodes(ensureIncomingNodes(parsedNodes));
    setEdges(parsedEdges.filter(e => e.to !== INCOMING_NODE_ID));
  }, []);

  const addNode = (type: IvrNodeType) => {
    if (type === 'incoming') return;
    const nt = nodeTypes.find(nt => nt.type === type);
    const id = generateId();
    // Place new nodes near current viewport center
    let x = 100 + nodes.length * 30;
    let y = 100 + nodes.length * 20;
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const center = clientToWorld(rect.left + rect.width / 2, rect.top + rect.height / 2);
      x = center.x - NODE_INNER_WIDTH / 2 + nodes.length * 16;
      y = center.y - 40 + nodes.length * 12;
    }
    const newNode: FlowNode = {
      id,
      type,
      x,
      y,
      label: nt?.title || type,
      config: getDefaultConfig(type)
    };
    setNodes([...nodes, newNode]);
    setSelectedId(id);
    setShowHelp(false);
  };

  const updateNode = (id: string, updates: Partial<FlowNode>) => {
    setNodes(prev => prev.map(n => (n.id === id ? { ...n, ...updates } : n)));
  };

  const deleteNode = (id: string) => {
    if (id === INCOMING_NODE_ID) return;
    setNodes(prev => prev.filter(n => n.id !== id));
    setEdges(prev => prev.filter(e => e.from !== id && e.to !== id));
    if (selectedId === id) setSelectedId(null);
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const typing = Boolean(target?.matches?.('input, textarea, select') || target?.isContentEditable)
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        selectedId &&
        selectedId !== INCOMING_NODE_ID &&
        !isConnecting &&
        !typing
      ) {
        e.preventDefault()
        deleteNode(selectedId)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [selectedId, isConnecting])

  const addEdge = (from: string, to: string, sourceHandle: string) => {
    if (to === INCOMING_NODE_ID) return;
    const handle = edgeHandleKey(sourceHandle);
    const exists = edges.some(
      e => e.from === from && e.to === to && edgeHandleKey(e.sourceHandle) === handle
    );
    if (!exists) {
      setEdges(prev => [...prev, { id: generateId(), from, to, sourceHandle: handle }]);
    }
  };

  const startConnectDrag = (e: MouseEvent, fromId: string, handleId: string) => {
    e.stopPropagation();
    e.preventDefault();
    setIsConnecting(true);
    setConnectFrom(fromId);
    setConnectFromHandle(handleId);
    setConnectMousePos(clientToWorld(e.clientX, e.clientY));
  };

  const handleConnectMouseMove = (e: MouseEvent) => {
    if (!isConnecting || !canvasRef.current) return;
    setConnectMousePos(clientToWorld(e.clientX, e.clientY));
  };

  const finishConnect = (toId: string) => {
    if (toId === INCOMING_NODE_ID) {
      cancelConnect();
      return;
    }
    if (connectFrom && connectFrom !== toId && connectFromHandle) {
      addEdge(connectFrom, toId, connectFromHandle);
    }
    setIsConnecting(false);
    setConnectFrom(null);
    setConnectFromHandle(null);
    setConnectMousePos(null);
    (window as any)._potentialTarget = null;
  };

  const endConnectDrag = (e: MouseEvent, toId: string) => {
    e.stopPropagation();
    finishConnect(toId);
  };

  const cancelConnect = () => {
    setIsConnecting(false);
    setConnectFrom(null);
    setConnectFromHandle(null);
    setConnectMousePos(null);
    (window as any)._potentialTarget = null;
  };

  const deleteEdge = (id: string) => {
    setEdges(prev => prev.filter(e => e.id !== id));
  };

  const save = async () => {
    const config = {
      nodes: ensureIncomingNodes(nodes).map(n => ({
        id: n.id,
        type: 'ivrNode',
        position: { x: n.x, y: n.y },
        data: { label: n.label, type: n.type, config: n.config }
      })),
      edges: edges.map(e => ({
        id: e.id,
        source: e.from,
        target: e.to,
        sourceHandle: e.sourceHandle
      })),
      start_node_id: INCOMING_NODE_ID
    };

    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch(saveUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrf || ''
        },
        body: JSON.stringify({ call_center_ivr_flow: { configuration: JSON.stringify(config) } })
      });
      if (!response.ok) {
        throw new Error(`Save failed with status ${response.status}`);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error('Save failed', e);
    }
  };

  const dragOffset = useRef<{ x: number; y: number } | null>(null);

  const handleHeaderMouseDown = (e: MouseEvent, id: string) => {
    if (isConnecting || spaceHeld.current) return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
      return;
    }
    e.stopPropagation();

    const node = nodes.find(n => n.id === id);
    if (node) {
      const world = clientToWorld(e.clientX, e.clientY);
      dragOffset.current = {
        x: world.x - node.x,
        y: world.y - node.y
      };
    }

    setDraggedId(id);
    setSelectedId(id);
  };

  const startPan = (e: MouseEvent) => {
    if (e.button === 1 || e.button === 0) {
      e.preventDefault();
      didPan.current = false;
      setIsPanning(true);
      panStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    }
  };

  const handleCanvasMouseDown = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target === canvasRef.current || target.dataset?.canvasBg) {
      startPan(e);
    } else if (e.button === 1 || (e.button === 0 && (spaceHeld.current || e.altKey))) {
      startPan(e);
    }
  };

  const handleNodeMouseMove = (e: MouseEvent) => {
    if (isPanning && panStart.current) {
      const dx = e.clientX - panStart.current.x;
      const dy = e.clientY - panStart.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didPan.current = true;
      setPan({
        x: panStart.current.panX + dx,
        y: panStart.current.panY + dy
      });
      return;
    }

    if (isConnecting) {
      handleConnectMouseMove(e);
      return;
    }

    if (!draggedId || !dragOffset.current) return;
    const world = clientToWorld(e.clientX, e.clientY);
    updateNode(draggedId, {
      x: world.x - dragOffset.current.x,
      y: world.y - dragOffset.current.y
    });
  };

  const handleMouseUp = () => {
    if (isConnecting && connectFrom && (window as any)._potentialTarget) {
      finishConnect((window as any)._potentialTarget);
    }
    setDraggedId(null);
    dragOffset.current = null;
    setIsPanning(false);
    panStart.current = null;
  };

  const zoomAt = (nextZoom: number, clientX?: number, clientY?: number) => {
    const z = clampZoom(nextZoom);
    if (!canvasRef.current) {
      setZoom(z);
      return;
    }
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = clientX ?? rect.left + rect.width / 2;
    const cy = clientY ?? rect.top + rect.height / 2;
    const wx = (cx - rect.left - pan.x) / zoom;
    const wy = (cy - rect.top - pan.y) / zoom;
    setPan({
      x: cx - rect.left - wx * z,
      y: cy - rect.top - wy * z
    });
    setZoom(z);
  };

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      zoomAt(zoom + delta, e.clientX, e.clientY);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoom, pan]);

  const fitView = () => {
    if (!canvasRef.current || nodes.length === 0) {
      setZoom(1);
      setPan({ x: 40, y: 40 });
      return;
    }
    const rect = canvasRef.current.getBoundingClientRect();
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    nodes.forEach(n => {
      const w = NODE_INNER_WIDTH + NODE_BORDER * 2;
      const h = getNodeMinHeight(n) + NODE_PADDING * 2 + NODE_BORDER * 2 + 40;
      minX = Math.min(minX, n.x);
      minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x + w);
      maxY = Math.max(maxY, n.y + h);
    });
    const pad = 80;
    const contentW = maxX - minX + pad * 2;
    const contentH = maxY - minY + pad * 2;
    const z = clampZoom(Math.min(rect.width / contentW, rect.height / contentH, 1));
    setZoom(z);
    setPan({
      x: (rect.width - (minX + maxX) * z) / 2,
      y: (rect.height - (minY + maxY) * z) / 2
    });
  };

  useEffect(() => {
    if (didInitialFit.current || nodes.length === 0) return;
    const el = canvasRef.current;
    if (!el) return;
    const run = () => {
      if (didInitialFit.current) return;
      const rect = el.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;
      didInitialFit.current = true;
      fitView();
    };
    run();
    const observer = new ResizeObserver(run);
    observer.observe(el);
    return () => observer.disconnect();
  }, [nodes]);

  const handleNodeClick = (e: MouseEvent, id: string) => {
    if (isConnecting) return;
    e.stopPropagation();
    setSelectedId(id);
    const target = e.target as HTMLElement | null;
    if (!target?.closest?.('[data-condition-help]')) setConditionHelpId(null);
  };

  const getConnections = (nodeId: string) => {
    const outgoing = edges.filter(e => e.from === nodeId);
    const incoming = edges.filter(e => e.to === nodeId);
    return { outgoing, incoming };
  };

  const toolbarBtnStyle: JSX.CSSProperties = {
    width: '32px',
    height: '32px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    background: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#475569'
  };

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Sidebar */}
      <div style={{ width: '220px', flexShrink: 0, background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)', color: 'white', padding: '20px', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <h2 style={{ fontSize: '18px', marginBottom: '20px', fontWeight: '600', flexShrink: 0 }}>🎙️ {t('canvas.title', 'Flow Designer')}</h2>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', marginRight: '-8px', paddingRight: '8px' }}>
          {isConnecting ? (
            <div style={{ background: 'rgba(59, 130, 246, 0.2)', padding: '12px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #3b82f6' }}>
              <div style={{ fontSize: '13px', marginBottom: '8px' }}>🔗 {t('canvas.drag_to_target', 'Drag to target point')}</div>
              <button
                onClick={cancelConnect}
                style={{ fontSize: '12px', background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer' }}
              >
                {t('nodes.common.cancel', 'Cancel')}
              </button>
            </div>
          ) : (
            <>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', marginBottom: '12px' }}>
                {t('canvas.add_elements', 'Add elements')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {nodeTypes.map(nt => (
                  <button
                    key={nt.type}
                    onClick={() => addNode(nt.type)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'left'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                  >
                    <span style={{ fontSize: '20px' }}>{nt.icon}</span>
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '13px' }}>{nt.title}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{nt.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div style={{ flexShrink: 0, paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <button
            onClick={save}
            style={{
              width: '100%',
              padding: '12px',
              background: saved ? '#10b981' : '#3b82f6',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {saved ? `✓ ${t('canvas.saved', 'Saved')}` : `💾 ${t('canvas.save', 'Save')}`}
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f8fafc', minWidth: 0 }}>
        <div style={{ padding: '12px 24px', background: 'white', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ color: '#64748b', fontSize: '14px' }}>
              {nodes.length} {t('canvas.element', 'element')} • {edges.length} {t('canvas.connection', 'connection')}
            </span>
            {showHelp && nodes.filter(n => n.type !== 'incoming').length === 0 && (
              <span style={{ color: '#3b82f6', fontSize: '13px' }}>
                👈 {t('canvas.select_from_sidebar', 'Select an element from the sidebar')}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
              <button type="button" onClick={() => zoomAt(zoom - ZOOM_STEP)} style={toolbarBtnStyle} title={t('canvas.zoom_out', 'Zoom out')}>−</button>
              <button
                type="button"
                onClick={() => zoomAt(1)}
                style={{ ...toolbarBtnStyle, width: '56px', fontSize: '12px', fontWeight: 600 }}
                title={t('canvas.zoom_reset', 'Reset zoom')}
              >
                {Math.round(zoom * 100)}%
              </button>
              <button type="button" onClick={() => zoomAt(zoom + ZOOM_STEP)} style={toolbarBtnStyle} title={t('canvas.zoom_in', 'Zoom in')}>+</button>
              <button type="button" onClick={fitView} style={{ ...toolbarBtnStyle, width: 'auto', padding: '0 10px', fontSize: '12px' }} title={t('canvas.fit_view', 'Fit view')}>
                {t('canvas.fit_view', 'Fit')}
              </button>
            </div>
            {selectedId && selectedId !== INCOMING_NODE_ID && !isConnecting && (
              <button
                type="button"
                onClick={() => deleteNode(selectedId)}
                style={{ color: '#ef4444', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}
              >
                🗑️ {t('nodes.common.delete', 'Delete element')}
              </button>
            )}
          </div>
        </div>

        <div
          ref={canvasRef}
          style={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            cursor: isPanning || spaceHeld.current ? 'grab' : isConnecting ? 'crosshair' : 'default',
            touchAction: 'none'
          }}
          onMouseMove={handleNodeMouseMove as any}
          onMouseDown={handleCanvasMouseDown as any}
          onMouseUp={handleMouseUp}
          onClick={() => {
            if (didPan.current) {
              didPan.current = false;
              return;
            }
            if (isConnecting) {
              cancelConnect();
            } else {
              setSelectedId(null);
              setConditionHelpId(null);
            }
          }}
        >
          {/* Infinite grid follows pan/zoom */}
          <div
            data-canvas-bg="1"
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: 'radial-gradient(circle, #cbd5e1 1px, transparent 1px)',
              backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
              backgroundPosition: `${pan.x}px ${pan.y}px`
            }}
          />

          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: '0 0',
              width: 1,
              height: 1
            }}
          >
            <svg
              style={{
                position: 'absolute',
                left: -4000,
                top: -4000,
                width: 12000,
                height: 12000,
                overflow: 'visible',
                pointerEvents: 'none'
              }}
            >
              <defs>
                <marker id="ivr-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="userSpaceOnUse">
                  <path d="M 0 0 L 8 4 L 0 8 z" fill="#94a3b8" />
                </marker>
              </defs>

              <g transform="translate(4000, 4000)">
                {edges.map(edge => {
                  const from = nodes.find(n => n.id === edge.from);
                  const to = nodes.find(n => n.id === edge.to);
                  if (!from || !to) return null;

                  const source = getHandlePosition(from, edgeHandleKey(edge.sourceHandle));
                  const target = getTargetPosition(to);
                  // Stop slightly before handle center so arrow tip sits on the port
                  const tipInset = HANDLE_SIZE / 2 + 2;
                  const angle = Math.atan2(target.y - source.y, target.x - source.x);
                  const endX = target.x - Math.cos(angle) * tipInset;
                  const endY = target.y - Math.sin(angle) * tipInset;
                  const path = buildEdgePath(source.x, source.y, endX, endY);
                  const midX = (source.x + endX) / 2;
                  const midY = (source.y + endY) / 2;

                  return (
                    <g key={edge.id}>
                      <path
                        d={path}
                        fill="none"
                        stroke="#94a3b8"
                        strokeWidth="2"
                        markerEnd="url(#ivr-arrow)"
                      />
                      <foreignObject
                        x={midX - 10}
                        y={midY - 10}
                        width="20"
                        height="20"
                        style={{ pointerEvents: 'auto', overflow: 'visible' }}
                      >
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            deleteEdge(edge.id);
                          }}
                          style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            fontSize: '11px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                            padding: 0
                          }}
                        >
                          ×
                        </button>
                      </foreignObject>
                    </g>
                  );
                })}

                {isConnecting && connectFrom && connectFromHandle && connectMousePos && (() => {
                  const from = nodes.find(n => n.id === connectFrom);
                  if (!from) return null;
                  const source = getHandlePosition(from, connectFromHandle);
                  return (
                    <path
                      d={buildEdgePath(source.x, source.y, connectMousePos.x, connectMousePos.y)}
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="2"
                      strokeDasharray="6,4"
                    />
                  );
                })()}
              </g>
            </svg>

            {nodes.map(node => {
              const nt = isIncomingNode(node) ? incomingNodeMeta() : nodeTypes.find(nt => nt.type === node.type);
              const isSelected = selectedId === node.id;
              const isConnectSource = connectFrom === node.id;
              const { outgoing } = getConnections(node.id);
              const sourceHandles = getSourceHandles(node);
              const minHeight = getNodeMinHeight(node);

              return (
                <div
                  key={node.id}
                  onClick={e => handleNodeClick(e, node.id)}
                  style={{
                    position: 'absolute',
                    left: node.x,
                    top: node.y,
                    width: `${NODE_CONTENT_WIDTH}px`,
                    minHeight: `${minHeight}px`,
                    background: 'white',
                    borderRadius: '12px',
                    padding: `${NODE_PADDING}px`,
                    boxSizing: 'content-box',
                    boxShadow: isSelected
                      ? '0 0 0 3px #3b82f6, 0 4px 20px rgba(0,0,0,0.15)'
                      : '0 2px 8px rgba(0,0,0,0.1)',
                    border: `2px solid ${isConnectSource ? '#3b82f6' : 'transparent'}`,
                    zIndex: isSelected || isConnectSource ? 10 : 1,
                    transition: 'box-shadow 0.2s'
                  }}
                >
                  {/* Target handle — center on left padding edge */}
                  {!isIncomingNode(node) && (
                  <div
                    onMouseUp={e => endConnectDrag(e, node.id)}
                    onMouseEnter={() => {
                      if (isConnecting && connectFrom !== node.id) {
                        (window as any)._potentialTarget = node.id;
                      }
                    }}
                    onMouseLeave={() => {
                      (window as any)._potentialTarget = null;
                    }}
                    style={{
                      position: 'absolute',
                      left: `${-HANDLE_SIZE / 2}px`,
                      top: `${HANDLE_TOP_BASE}px`,
                      width: `${HANDLE_SIZE}px`,
                      height: `${HANDLE_SIZE}px`,
                      borderRadius: '50%',
                      background: isConnecting && connectFrom !== node.id ? '#10b981' : '#cbd5e1',
                      border: '2px solid white',
                      cursor: isConnecting && connectFrom !== node.id ? 'pointer' : 'default',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transform: isConnecting && connectFrom !== node.id ? 'scale(1.3)' : 'scale(1)',
                      transition: 'transform 0.2s',
                      pointerEvents: 'auto',
                      zIndex: 100,
                      margin: 0,
                      boxSizing: 'border-box'
                    }}
                    title={t('canvas.target', 'Target')}
                  />
                  )}

                  {/* Source handles — centers on right padding edge */}
                  {sourceHandles.map((handle, index) => {
                    const isActiveHandle = isConnectSource && connectFromHandle === handle.id;
                    const hasOutgoing = outgoing.some(e => edgeHandleKey(e.sourceHandle) === handle.id);

                    return (
                      <div
                        key={handle.id}
                        onMouseDown={e => !isConnecting && startConnectDrag(e, node.id, handle.id)}
                        style={{
                          position: 'absolute',
                          left: `${NODE_INNER_WIDTH - HANDLE_SIZE / 2}px`,
                          top: `${handleTop(node, index)}px`,
                          width: `${HANDLE_SIZE}px`,
                          height: `${HANDLE_SIZE}px`,
                          borderRadius: '50%',
                          background: isActiveHandle ? '#10b981' : handle.badgeColor,
                          border: '2px solid white',
                          cursor: isConnecting ? 'default' : 'pointer',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transform: isActiveHandle ? 'scale(1.3)' : 'scale(1)',
                          transition: 'transform 0.2s',
                          pointerEvents: 'auto',
                          zIndex: 100,
                          margin: 0,
                          opacity: hasOutgoing ? 1 : 0.85,
                          boxSizing: 'border-box'
                        }}
                        title={
                          handle.caption
                            || (node.type === 'menu' && handle.id === 'timeout'
                              ? t('nodes.menu.timeout_handle', 'Timeout / default')
                              : t('canvas.connect', 'Connect'))
                        }
                      >
                        {handle.label && (
                          <span
                            style={{
                              color: handle.textColor || '#ffffff',
                              fontSize: handle.label.length > 2 ? '8px' : '10px',
                              fontWeight: '600',
                              lineHeight: 1,
                              userSelect: 'none'
                            }}
                          >
                            {handle.label}
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {!isConnecting && (
                    <div
                      onMouseDown={e => handleHeaderMouseDown(e, node.id)}
                      title={t('canvas.drag_to_move', 'Drag to move')}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '24px',
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.03) 0%, transparent 100%)',
                        borderRadius: '12px 12px 0 0',
                        cursor: draggedId === node.id ? 'grabbing' : 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '3px'
                      }}
                    >
                      <span style={{ width: '24px', height: '3px', background: '#cbd5e1', borderRadius: '2px' }} />
                    </div>
                  )}

                  <div
                    onMouseDown={!isConnecting ? e => handleHeaderMouseDown(e, node.id) : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginTop: '12px',
                      marginBottom: '10px',
                      padding: '0 8px',
                      height: '44px',
                      boxSizing: 'border-box',
                      marginLeft: '-8px',
                      marginRight: '-8px',
                      borderRadius: '8px',
                      cursor: isConnecting ? 'default' : draggedId === node.id ? 'grabbing' : 'grab',
                      background: isSelected ? 'rgba(59, 130, 246, 0.05)' : 'transparent'
                    }}
                  >
                    <span style={{ fontSize: '24px', userSelect: 'none' }}>{nt?.icon}</span>
                    <input
                      value={node.label}
                      onChange={e => updateNode(node.id, { label: (e.target as HTMLInputElement).value })}
                      onMouseDown={e => e.stopPropagation()}
                      disabled={isConnecting || isIncomingNode(node)}
                      style={{
                        flex: 1,
                        border: 'none',
                        fontWeight: '600',
                        fontSize: '14px',
                        background: 'transparent',
                        borderBottom: isSelected ? '2px solid #3b82f6' : '2px solid transparent',
                        padding: '4px',
                        outline: 'none',
                        cursor: isConnecting ? 'default' : 'text',
                        minWidth: '120px'
                      }}
                    />
                    {node.type === 'condition' && (
                      <button
                        type="button"
                        data-condition-help="1"
                        aria-label={t('nodes.condition.usage_title', 'How to use')}
                        title={t('nodes.condition.usage_title', 'How to use')}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => {
                          e.stopPropagation();
                          setConditionHelpId(prev => (prev === node.id ? null : node.id));
                        }}
                        style={{
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          border: '1px solid #cbd5e1',
                          background: conditionHelpId === node.id ? '#6366f1' : '#f8fafc',
                          color: conditionHelpId === node.id ? '#ffffff' : '#6366f1',
                          fontSize: '13px',
                          fontWeight: 700,
                          fontStyle: 'italic',
                          fontFamily: 'Georgia, serif',
                          cursor: 'pointer',
                          flexShrink: 0,
                          lineHeight: 1,
                          padding: 0
                        }}
                      >
                        i
                      </button>
                    )}
                  </div>

                  {sourceHandles.some(h => h.caption) && (
                    <div>
                      {sourceHandles.map(handle => (
                        <div
                          key={handle.id}
                          style={{
                            height: `${BRANCH_ROW}px`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            paddingRight: '10px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: handle.badgeColor,
                            lineHeight: 1,
                            userSelect: 'none'
                          }}
                        >
                          {handle.caption}
                        </div>
                      ))}
                    </div>
                  )}

                  {isSelected && !isConnecting && !isIncomingNode(node) && (
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                      <QuickConfig node={node} onChange={config => updateNode(node.id, { config })} />
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation()
                          deleteNode(node.id)
                        }}
                        onMouseDown={e => e.stopPropagation()}
                        style={{
                          marginTop: '12px',
                          width: '100%',
                          padding: '8px 10px',
                          fontSize: '13px',
                          fontWeight: 500,
                          color: '#ef4444',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        🗑️ {t('nodes.common.delete', 'Delete element')}
                      </button>
                    </div>
                  )}

                  {conditionHelpId === node.id && (
                    <div
                      data-condition-help="1"
                      onMouseDown={e => e.stopPropagation()}
                      onClick={e => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: '100%',
                        marginTop: '8px',
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        zIndex: 50,
                        fontSize: '12px',
                        color: '#334155',
                        lineHeight: 1.45,
                        textAlign: 'left',
                        cursor: 'default'
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: '6px' }}>
                        {t('nodes.condition.usage_title', 'How to use')}
                      </div>
                      {['usage_intro', 'usage_vars', 'usage_example', 'usage_caller'].map(key => (
                        <p key={key} style={{ margin: '0 0 8px' }}>
                          {t(`nodes.condition.${key}`, key)}
                        </p>
                      ))}
                    </div>
                  )}

                  {outgoing.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '10px',
                        background: '#3b82f6',
                        color: 'white',
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: '10px'
                      }}
                    >
                      {outgoing.length}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div
            style={{
              position: 'absolute',
              bottom: '12px',
              left: '12px',
              fontSize: '11px',
              color: '#94a3b8',
              background: 'rgba(255,255,255,0.9)',
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              pointerEvents: 'none'
            }}
          >
            {t('canvas.pan_hint', 'Scroll = zoom · Drag empty canvas to pan · Delete/Backspace removes selected')}
          </div>
        </div>
      </div>
    </div>
  );
};

const inputStyle: JSX.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: '13px',
  border: '1px solid #cbd5e1',
  borderRadius: '6px',
  cursor: 'text'
};

const textareaStyle: JSX.CSSProperties = {
  ...inputStyle,
  minHeight: '50px',
  resize: 'vertical'
};

const labelStyle: JSX.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: '500',
  color: '#475569',
  marginBottom: '4px'
};

const QuickConfig: FunctionComponent<{ node: FlowNode; onChange: (c: Record<string, unknown>) => void }> = ({
  node,
  onChange
}) => {
  const c = node.config || {};
  const bootGroups = (): Array<{ id: number; name: string }> => {
    const g = (window as any).IvrDesignerPersonGroups;
    return Array.isArray(g) ? g : [];
  };
  const bootPeople = (): Array<{ id: number; name: string }> => {
    const p = (window as any).IvrDesignerPeople;
    return Array.isArray(p) ? p : [];
  };
  const [personGroups, setPersonGroups] = useState(bootGroups);
  const [people, setPeople] = useState(bootPeople);

  useEffect(() => {
    const needsGroups = node.type === 'set_person_group' || node.type === 'transfer';
    const needsPeople = node.type === 'transfer';
    if (!needsGroups && !needsPeople) return;

    // Prefer page-bootstrapped lists (reliable with cookie session).
    const seededGroups = bootGroups();
    const seededPeople = bootPeople();
    if (needsGroups && seededGroups.length) setPersonGroups(seededGroups);
    if (needsPeople && seededPeople.length) setPeople(seededPeople);

    const base = (window as any).IvrCallCenterApiBase || '/api/call_center';
    let cancelled = false;

    if (needsGroups && !seededGroups.length) {
      fetch(`${base}/person_groups`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then((r) => (r.ok ? r.json() : { person_groups: [] }))
        .then((data) => {
          if (!cancelled) setPersonGroups(data.person_groups || []);
        })
        .catch(() => {
          if (!cancelled) setPersonGroups([]);
        });
    }

    if (needsPeople && !seededPeople.length) {
      fetch(`${base}/people`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then((r) => (r.ok ? r.json() : { people: [] }))
        .then((data) => {
          if (!cancelled) setPeople(data.people || []);
        })
        .catch(() => {
          if (!cancelled) setPeople([]);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [node.type]);

  const stopPropagation = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const varPlaceholder = t('nodes.common.variable_hint', 'Use $variableName for substitution');

  const field = (
    label: string,
    key: string,
    type: 'text' | 'textarea' | 'select' = 'text',
    options?: Array<string | { value: string; label: string }>,
    placeholder?: string
  ) => (
    <div style={{ marginBottom: '10px' }} onMouseDown={stopPropagation}>
      <label style={labelStyle}>{label}</label>
      {type === 'textarea' ? (
        <textarea
          value={(c[key] as string) || ''}
          onChange={e => onChange({ ...c, [key]: (e.target as HTMLTextAreaElement).value })}
          onMouseDown={stopPropagation}
          placeholder={placeholder}
          style={textareaStyle}
        />
      ) : type === 'select' ? (
        <select
          value={(c[key] as string) || ''}
          onChange={e => onChange({ ...c, [key]: (e.target as HTMLSelectElement).value })}
          onMouseDown={stopPropagation}
          style={{ ...inputStyle, cursor: 'pointer', background: 'white' }}
        >
          {options?.map(o => {
            const value = typeof o === 'string' ? o : o.value;
            const labelText = typeof o === 'string' ? o : o.label;
            return <option key={value} value={value}>{labelText}</option>;
          })}
        </select>
      ) : (
        <input
          type="text"
          value={(c[key] as string) || ''}
          onChange={e => onChange({ ...c, [key]: (e.target as HTMLInputElement).value })}
          onMouseDown={stopPropagation}
          placeholder={placeholder}
          style={inputStyle}
        />
      )}
    </div>
  );

  const varField = (label: string, key: string, help?: string) => (
    <div style={{ marginBottom: '10px' }} onMouseDown={stopPropagation}>
      <label style={labelStyle}>{label}</label>
      <input
        type="text"
        value={toVarDisplay((c[key] as string) || '')}
        onChange={e => onChange({ ...c, [key]: fromVarInput((e.target as HTMLInputElement).value) })}
        onMouseDown={stopPropagation}
        placeholder={varPlaceholder}
        style={inputStyle}
      />
      {help && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>{help}</div>}
    </div>
  );

  const operatorTarget = String(c.operatorTarget || 'rules');
  const transferType = String(c.transferType || 'operator');

  switch (node.type) {
    case 'incoming':
      return null;

    case 'play_message':
      return field(
        t('nodes.play_message.message_text', 'TTS message'),
        'content',
        'textarea',
        undefined,
        varPlaceholder
      );

    case 'voicemail':
      return field(
        t('nodes.voicemail.prompt', 'TTS message'),
        'introMessage',
        'textarea',
        undefined,
        varPlaceholder
      );

    case 'menu': {
      const options = (c.options as MenuOption[]) || [];
      const timeoutSeconds = Number(c.timeoutSeconds ?? 10);
      const maxAttempts = Number(c.maxAttempts ?? 3);
      return (
        <>
          {field(
            t('nodes.menu.intro_message', 'Introduction message'),
            'introMessage',
            'textarea',
            undefined,
            varPlaceholder
          )}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }} onMouseDown={stopPropagation}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('nodes.menu.timeout', 'Timeout (seconds)')}</label>
              <input
                type="number"
                min={1}
                max={120}
                value={timeoutSeconds}
                onChange={e => {
                  const v = parseInt((e.target as HTMLInputElement).value, 10);
                  onChange({ ...c, timeoutSeconds: Number.isFinite(v) && v > 0 ? v : 10 });
                }}
                onMouseDown={stopPropagation}
                style={inputStyle}
              />
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                {t('nodes.menu.timeout_help', 'Seconds to wait for a keypress')}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('nodes.menu.max_attempts', 'Retries before timeout')}</label>
              <input
                type="number"
                min={1}
                max={10}
                value={maxAttempts}
                onChange={e => {
                  const v = parseInt((e.target as HTMLInputElement).value, 10);
                  onChange({ ...c, maxAttempts: Number.isFinite(v) && v > 0 ? v : 3 });
                }}
                onMouseDown={stopPropagation}
                style={inputStyle}
              />
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                {t('nodes.menu.max_attempts_help', 'How many times to repeat the menu before the timeout branch')}
              </div>
            </div>
          </div>
          <div style={{ marginBottom: '10px' }} onMouseDown={stopPropagation}>
            <label style={labelStyle}>{t('nodes.menu.options', 'Menu options')}</label>
            {options.map((opt, index) => (
              <div
                key={index}
                style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}
              >
                <input
                  type="text"
                  value={opt.dtmf}
                  onChange={e => {
                    const next = [...options];
                    next[index] = { ...next[index], dtmf: (e.target as HTMLInputElement).value };
                    onChange({ ...c, options: next });
                  }}
                  onMouseDown={stopPropagation}
                  placeholder={t('nodes.menu.dtmf_key', 'Key')}
                  style={{ ...inputStyle, width: '48px', flexShrink: 0, textAlign: 'center' }}
                />
                <input
                  type="text"
                  value={opt.label}
                  onChange={e => {
                    const next = [...options];
                    next[index] = { ...next[index], label: (e.target as HTMLInputElement).value };
                    onChange({ ...c, options: next });
                  }}
                  onMouseDown={stopPropagation}
                  placeholder={t('nodes.menu.option_label', 'Label')}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const next = options.filter((_, i) => i !== index);
                    onChange({ ...c, options: next.length ? next : [{ dtmf: '1', label: '' }] });
                  }}
                  onMouseDown={stopPropagation}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ef4444',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '0 4px'
                  }}
                  title={t('nodes.common.delete', 'Delete')}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const nextDtmf = String(options.length + 1);
                onChange({ ...c, options: [...options, { dtmf: nextDtmf, label: '' }] });
              }}
              onMouseDown={stopPropagation}
              style={{
                fontSize: '12px',
                padding: '6px 10px',
                background: '#f1f5f9',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#475569'
              }}
            >
              + {t('nodes.menu.add_option', 'Add option')}
            </button>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>
              {t('nodes.menu.timeout_handle', 'Timeout / default')} (•)
            </div>
          </div>
        </>
      );
    }

    case 'collect_input':
      return (
        <>
          {field(
            t('nodes.collect_input.prompt', 'TTS prompt'),
            'promptMessage',
            'textarea',
            undefined,
            varPlaceholder
          )}
          {varField(
            t('nodes.collect_input.variable_name', 'Variable name'),
            'variableName',
            t('nodes.collect_input.variable_help', 'Collected value stored in this variable')
          )}
        </>
      );

    case 'ai_agent':
      return (
        <>
          {field(
            t('nodes.ai_agent.system_prompt', 'System prompt'),
            'systemPrompt',
            'textarea',
            undefined,
            t('nodes.ai_agent.system_prompt_hint', 'Instructions for the AI. Use $variable for caller data substitution.')
          )}
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '-4px', marginBottom: '10px' }}>
            {t(
              'nodes.ai_agent.hostagent_merge_note',
              'Merged with the backend call_center Ai::SystemPrompt (Admin). Optional TTS welcome is configured separately.'
            )}
          </div>
        </>
      );

    case 'transfer':
      return (
        <>
          {field(
            t('nodes.transfer.transfer_type', 'Transfer type'),
            'transferType',
            'select',
            [
              { value: 'operator', label: t('nodes.transfer.type_operator', 'Operator (browser)') },
              { value: 'number', label: t('nodes.transfer.type_number', 'Phone number') }
            ]
          )}
          {transferType === 'number' &&
            field(t('nodes.transfer.destination', 'Phone number'), 'destination')}
          {transferType === 'operator' && (
            <>
              {field(
                t('nodes.transfer.operator_target', 'Operator target'),
                'operatorTarget',
                'select',
                [
                  { value: 'rules', label: t('nodes.transfer.target_rules', 'Routing rules (default)') },
                  { value: 'person_group', label: t('nodes.transfer.target_person_group', 'Person group') },
                  { value: 'person', label: t('nodes.transfer.target_person', 'Specific person') }
                ]
              )}
              {operatorTarget === 'person_group' && (
                <div style={{ marginBottom: '10px' }} onMouseDown={stopPropagation}>
                  <label style={labelStyle}>{t('nodes.set_person_group.person_group', 'Person group')}</label>
                  <select
                    value={String(c.personGroupId || '')}
                    onChange={e => onChange({ ...c, personGroupId: (e.target as HTMLSelectElement).value })}
                    onMouseDown={stopPropagation}
                    style={{ ...inputStyle, cursor: 'pointer', background: 'white' }}
                  >
                    <option value="">{t('nodes.set_person_group.select', 'Select group…')}</option>
                    {personGroups.map(g => (
                      <option key={g.id} value={String(g.id)}>{g.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {operatorTarget === 'person' && (
                <div style={{ marginBottom: '10px' }} onMouseDown={stopPropagation}>
                  <label style={labelStyle}>{t('nodes.transfer.person', 'Person')}</label>
                  <select
                    value={String(c.personId || '')}
                    onChange={e => onChange({ ...c, personId: (e.target as HTMLSelectElement).value })}
                    onMouseDown={stopPropagation}
                    style={{ ...inputStyle, cursor: 'pointer', background: 'white' }}
                  >
                    <option value="">{t('nodes.transfer.select_person', 'Select person…')}</option>
                    {people.map(p => (
                      <option key={p.id} value={String(p.id)}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '10px' }}>
                {operatorTarget === 'rules'
                  ? t(
                    'nodes.transfer.operator_help',
                    'Rings BC responsible if known, otherwise the current/default person group.'
                  )
                  : operatorTarget === 'person_group'
                    ? t('nodes.transfer.override_group_help', 'Rings members of the selected person group.')
                    : t('nodes.transfer.override_person_help', 'Rings only the selected person (must have a login).')}
              </div>
            </>
          )}
        </>
      );

    case 'set_person_group':
      return (
        <div style={{ marginBottom: '10px' }} onMouseDown={stopPropagation}>
          <label style={labelStyle}>{t('nodes.set_person_group.person_group', 'Person group')}</label>
          <select
            value={String(c.personGroupId || '')}
            onChange={e => onChange({ ...c, personGroupId: (e.target as HTMLSelectElement).value })}
            onMouseDown={stopPropagation}
            style={{ ...inputStyle, cursor: 'pointer', background: 'white' }}
          >
            <option value="">{t('nodes.set_person_group.select', 'Select group…')}</option>
            {personGroups.map(g => (
              <option key={g.id} value={String(g.id)}>{g.name}</option>
            ))}
          </select>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
            {t(
              'nodes.set_person_group.help',
              'Overrides the flow default operator group for later operator transfer.'
            )}
          </div>
        </div>
      );

    case 'identify_caller':
      return (
        <div style={{ fontSize: '12px', color: '#475569', lineHeight: 1.45 }}>
          {t(
            'nodes.identify_caller.help',
            'Matches the caller phone number. One person takes the recognized branch. No match, several matches, or a bad number takes the unknown branch. Later steps can use $caller_name and $caller_recognized.'
          )}
        </div>
      );

    case 'condition':
      return (
        <>
          {varField(
            t('nodes.condition.variable', 'Variable'),
            'variableName',
            t('nodes.condition.variable_help', 'Previously collected variable (e.g. $customer_number)')
          )}
          {field(
            t('nodes.condition.operator', 'Operator'),
            'operator',
            'select',
            ['equals', 'not_equals', 'contains']
          )}
          <div style={{ marginBottom: '10px' }} onMouseDown={stopPropagation}>
            <label style={labelStyle}>{t('nodes.condition.value', 'Value')}</label>
            <input
              type="text"
              value={(c.value as string) || ''}
              onChange={e => onChange({ ...c, value: (e.target as HTMLInputElement).value })}
              onMouseDown={stopPropagation}
              placeholder={t('nodes.condition.value_hint', 'Literal or $otherVar')}
              style={inputStyle}
            />
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
              {t('nodes.condition.value_help', 'Comparison value')}
            </div>
          </div>
        </>
      );

    default:
      return null;
  }
};
